import {useState} from 'react';
import {Image, Platform, Pressable, StyleSheet, Text} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import {ImagePickerAsset, ImagePickerOptions} from 'expo-image-picker';
import Page from "@/lib/pages/Page";
import ActionButton from "@/lib/components/ActionButton";
import {Color} from "@/lib/styles";
import {uploadPicture} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {UploadResponse} from "@/lib/types";
import Nav from "@/lib/components/Nav";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Center from "@/lib/components/Center";
import {File} from 'expo-file-system';

const imagePickerOptions: ImagePickerOptions = {
    mediaTypes: ['images'],
    allowsMultipleSelection: false,
    allowsEditing: false,
    quality: 1,
    exif: false,
    selectionLimit: 1,
    // prevent iOS from uploading HEIF
    preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
};

type Props = {
    onUpload: (response: UploadResponse) => void,
}

export default function ImageUpload({onUpload}: Props) {
    const [image, setImage] = useState<ImagePickerAsset | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const {session} = useSession();

    const pickImage = async () => {
        try {
            setError(null);
            const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (!permissionResult.granted) {
                setError(new Error("Har ikke tilgang til bildegalleri.\n\nSjekk innstillinger og forsøk igjen."));
                return;
            }
            let result = await ImagePicker.launchImageLibraryAsync(imagePickerOptions);
            if (!result.canceled && result.assets.length === 1) {
                setImage(result.assets[0]);
            }
        } catch (err) {
            setError(err as Error)
        }
    };

    async function takeImage() {
        try {
            setError(null);
            const permissionResult = await ImagePicker.requestCameraPermissionsAsync();
            if (!permissionResult.granted) {
                setError(new Error("Har ikke tilgang til kamera.\n\nSjekk innstillinger og forsøk igjen."));
                return;
            }
            let result = await ImagePicker.launchCameraAsync(imagePickerOptions);
            if (!result.canceled && result.assets.length === 1) {
                setImage(result.assets[0]);
            }
        } catch (err) {
            setError(err as Error)
        }
    }

    async function uploadImage() {
        try {
            setError(null);
            setBusy(true);
            if (!image) {
                setError(new Error("Vennligst velg et bilde før du laster opp."));
                return;
            }
            if (!session) {
                setError(new Error("Du må være innlogget for å laste opp bilder."));
                return;
            }
            let payload;
            if (image.file) {
                payload = image.file;
            } else {
                const imageData = new File(image.uri);
                const contents = await imageData.arrayBuffer();
                payload = new Uint8Array(contents);
            }
            const response = await uploadPicture(payload, session);
            onUpload(response);
        } catch (err) {
            setError(err as Error)
        } finally {
            setBusy(false);
        }
    }

    return (
        <Page>
            <Center>
                {!image && (
                    <Pressable style={styles.image}>
                        <Text style={styles.placeholder}>Bilde ikke valgt</Text>
                    </Pressable>
                )}
                {image && <Image source={{uri: image.uri}} style={styles.image}/>}
                {error && <ErrorMessage error={error}/>}
                <Nav>
                    {!busy && Platform.OS !== "web" && <ActionButton icon={"camera"} onPress={takeImage}/>}
                    {!busy && <ActionButton title={"Velg bilde"} onPress={pickImage}/>}
                    {image &&
                        <ActionButton title={"Last opp"} icon={"checkmark"} onPress={uploadImage} color={Color.Green}
                                      busy={busy}/>}
                </Nav>
            </Center>
        </Page>
    );
}

const styles = StyleSheet.create({
    image: {
        width: 300,
        height: 300,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: Color.Faded,
        borderRadius: 8,
        marginLeft: "auto",
        marginRight: "auto",
    },
    placeholder: {
        color: Color.FadedText,
        fontSize: 32,
        width: "100%",
        lineHeight: 300,
        textAlign: "center",
    }
});